<template>
  <div
    class="calendar-scroll-month"
    :data-month="month"
  >
    <div
      v-show="isLoading"
      class="loading-month-wrapper"
    >
      <span
        role="presentation"
        tabindex="0"
      > {{ loadingMessage }}</span>
      <loading-dots />
    </div>
    <button
      ref="title"
      type="button"
      class="calendar-scroll-month-title"
      data-component="calendar-scroll-month-title"
      :tabindex="isVisible ? '0' : '-1'"
      @click.prevent="$emit('month-click')"
      @space.prevent="$emit('month-click')"
      @enter.prevent="$emit('month-click')"
    >
      {{ tc(`calendar.months.${monthIndex}`) }} {{ year }}
    </button>
    <div
      class="calendar-scroll-month-days-wrapper"
      @click.prevent.stop="updateField"
      @keydown.enter.prevent.stop="updateField"
      @keydown.space.prevent.stop="updateField"
    >
      <button
        v-for="day in daysInMonth"
        :id="day.id"
        :key="day.id"
        class="calendar-scroll-day"
        :data-day="day.dayFormatted"
        :class="[
          { error: errorStatus(day.dayFormatted) },
          { 'is-start-date': isStartDate(day.dayFormatted) },
          { 'is-end-date': isEndDate(day.dayFormatted) },
          { 'is-between-date': isBetweenDate(day.dayFormatted) },
          { 'is-selected': isSelected(day.dayFormatted) },
          { 'not-available': dayStatus(day.dayFormatted) === 'Close' },
          {
            'is-past-week':
              isPastDate(day.dayFormatted) && !dayIsInSameWeek(day.dayFormatted),
          },
          { 'disabled': isDisabled(day.dayFormatted) },
        ]"
        :style="[
          { 'margin-left': getMarginLeft(day.dayFormatted) },
        ]"
        :aria-disabled="isDisabled(day.dayFormatted)"
        :tabindex="getDayTabIndex(day.dayFormatted)"
        :aria-label="ariaLabel(day.dayFormatted)"
        :aria-pressed="isSelected(day.dayFormatted)"
        @focus="bindKeyboardEvents"
        @blur="unbindKeyboardEvents"
      >
        <div
          v-show="showBackground"
          class="calendar-scroll-day-background no-pointer-events"
          aria-hidden="true"
        />
        <div
          class="calendar-scroll-day-selected no-pointer-events"
          aria-hidden="true"
        />
        <span
          class="calendar-scroll-day-label no-pointer-events"
        >
          {{ day.day }}
        </span>
        <span
          v-show="showPrice(day.dayFormatted)"
          class="calendar-scroll-day-price no-pointer-events"
          :aria-hidden="!showPrice(day.dayFormatted)"
        >
          {{ displayPrice(day.dayFormatted) }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import { mapGetters, mapState } from 'vuex'
  import CalendarDayMixin from '@mixins/booking-widget/calendar-scroll/calendar-day'

  export default {
    name: 'CalendarMonth',
    mixins: [CalendarDayMixin],
    props: {
      month: {
        type: String,
        required: true,
      },
      daysInMonth: {
        type: Array,
        required: true,
      },
      inputSelected: {
        type: String,
        required: true,
      },
      isSticky: {
        type: Boolean,
      },
      isFirst: {
        type: Boolean,
      },
      isVisible: {
        type: Boolean,
      },
    },
    data: () => ({
      width: 0,
      dayWidth: 0,
    }),
    computed: {
      ...mapState('booking', ['calendarLoading', 'calendarDates']),
      ...mapState('responsive', ['check']),
      ...mapGetters('booking', ['monthFormat', 'hotelCalendarDates']),
      ...mapGetters('i18n', ['tc']),
      monthInDate() {
        return moment(this.month, this.monthFormat)
      },
      year() {
        return this.monthInDate.year()
      },
      monthIndex() {
        return this.monthInDate.month() + 1
      },
      loadingMessage() {
        return this.tc('booking.loadingMessage', {
          month: this.tc(`calendar.months.${this.monthIndex}`),
        })
      },
      isLoading() {
        return this.month === this.calendarLoading
      },
      monthData() {
        return this.hotelCalendarDates(this.localQuery, this.month)
      },
    },
    mounted() {
      const elBounding = this.$el.getBoundingClientRect()
      this.width = elBounding.width
      this.dayWidth = this.width / 7 - 16
    },
    methods: {
      async updateField(e) {
        e.preventDefault()
        e.stopPropagation()
        if (!e.target.dataset.day || this.isDisabled(e.target.dataset.day)) return

        const { day } = e.target.dataset
        const dayInDate = this.dayInDate(day)

        this.updateLocalQuery({
          prop: this.inputSelected,
          value: dayInDate.format(this.dayFormat),
        })
        this.$emit('day-selected')

        this.$nextTick(() => {
          const targetClass = this.inputSelected === 'stayDateStart' ? 'is-start-date' : 'is-end-date'
          const targetElement = document.querySelector(`.${targetClass}`)

          if (targetElement) {
            targetElement.focus()
            if (this.check.isMdAndDown) this.scrollToItem(targetElement)
          }
        })
      },
      scrollToItem(dayElement) {
        const container = document.querySelector('.calendar-scroll-months')

        if (container && dayElement) {
          const containerRect = container.getBoundingClientRect()
          const dayRect = dayElement.getBoundingClientRect()

          const containerCenterStart = containerRect.top + containerRect.height * 0.2
          const containerCenterEnd = containerRect.top + containerRect.height * 0.8

          if (dayRect.top < containerCenterStart || dayRect.bottom > containerCenterEnd) {
            const scrollPosition = container.scrollTop + (dayRect.top - containerRect.top) - (container.offsetHeight / 2) + (dayElement.offsetHeight / 2)

            container.scrollTo({
              top: scrollPosition,
              behavior: 'smooth',
            })
          }
        }
      },
    }
  }
</script>
